import { AuthError } from "@vidario/api-client";
import { Button, Input, VidarioLogo } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { useAuth } from "../../providers/auth-provider";

const schema = z.object({
  email: z.string().email("Bitte eine gültige E-Mail-Adresse eingeben."),
});
type ForgotInput = z.infer<typeof schema>;

export function ForgotPasswordRoute(): ReactElement {
  const { resetPasswordForEmail } = useAuth();
  const [sent, setSent] = useState(false);
  const [rateLimited, setRateLimited] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotInput>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  // Always show the success state — surfacing a "user not found" error
  // here would let attackers enumerate which addresses have accounts.
  // Genuine failures (network, backend) are also swallowed; the user
  // can retry if no mail arrives. The one exception is the per-IP rate
  // limit (#67): it says nothing about the address, and pretending a mail
  // went out would leave the user waiting for nothing.
  const onSubmit = async (values: ForgotInput): Promise<void> => {
    setRateLimited(null);
    try {
      await resetPasswordForEmail(values.email);
    } catch (err) {
      if (err instanceof AuthError && err.code === "rate_limited") {
        setRateLimited(err.message);
        return;
      }
      // intentional: never reveal whether the address exists
    }
    setSent(true);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-app px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <VidarioLogo size={36} />
        </div>
        <div className="rounded-lg border border-border-default bg-neutral-0 p-8 shadow-sm">
          <h1 className="m-0 text-2xl font-semibold -tracking-[0.3px]">Passwort vergessen?</h1>
          <p className="mt-1 mb-6 text-sm text-text-secondary">
            Wir schicken dir einen Link zum Zurücksetzen per E-Mail.
          </p>

          {sent ? (
            <div
              role="status"
              className="rounded-md border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700"
              data-testid="forgot-success"
            >
              Falls für die angegebene Adresse ein Konto existiert, liegt dir in Kürze eine E-Mail
              vor. Bitte prüfe auch den Spam-Ordner.
            </div>
          ) : (
            <form
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              className="flex flex-col gap-4"
              noValidate
            >
              <Input
                label="E-Mail"
                type="email"
                autoComplete="email"
                required
                {...register("email")}
                error={errors.email?.message}
              />
              {rateLimited && (
                <p role="alert" className="text-sm text-danger-600">
                  {rateLimited}
                </p>
              )}
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                Link senden
              </Button>
            </form>
          )}

          <div className="mt-5 text-center text-sm">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              Zurück zur Anmeldung
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
